import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  formalizedZipSelectors,
  getAllFormalizedZips,
  getFormalizedZipById,
  createFormalizedZip,
  updateFormalizedZip,
  deleteFormalizedZip,
  filterFormalizedZips,
} from 'entities/formalizedZip';
import { FormalizedZipFilterRequest } from 'shared/api';
import { FormalizedZipRequest } from 'entities/formalizedZip/model/types/types';
import { useGlobalSpinner } from 'shared/hooks';

export const useFormalizedZip = () => {
  const dispatch = useAppDispatch();
  const formalizedZips = useAppSelector(formalizedZipSelectors.selectFormalizedZipData);
  const isLoading = useAppSelector(formalizedZipSelectors.selectFormalizedZipLoading);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const detail = useAppSelector(formalizedZipSelectors.getFormalizedZipDetail);
  const [detailError, setDetailError] = useState<unknown>(null);

  const loadFormalizedZips = useCallback(async (filter?: FormalizedZipFilterRequest) => {
    try {
      showSpinner('Загрузка формализованных ЗИП...');
      if (filter && Object.keys(filter).length > 0) {
        const data = await dispatch(filterFormalizedZips(filter)).unwrap();
        return data;
      }
      const data = await dispatch(getAllFormalizedZips()).unwrap();
      return data;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const createNew = useCallback(async (data: FormalizedZipRequest) => {
    try {
      showSpinner('Создание формализованного ЗИП...');
      const result = await dispatch(createFormalizedZip(data)).unwrap();
      return result;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const updateExisting = useCallback(async (id: number, data: FormalizedZipRequest) => {
    try {
      showSpinner('Обновление формализованного ЗИП...');
      const result = await dispatch(updateFormalizedZip({ id, data })).unwrap();
      return result;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const deleteById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление формализованного ЗИП...');
      const result = await dispatch(deleteFormalizedZip(id)).unwrap();
      return result;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailError(null);
    try {
      showSpinner('Загрузка данных формализованного ЗИП...');
      const d = await dispatch(getFormalizedZipById(id)).unwrap();
      return { success: true, detail: d } as const;
    } catch (error) {
      setDetailError(error);
      return { success: false, error } as const;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  return {
    formalizedZips,
    isLoading,
    loadFormalizedZips,
    createNew,
    updateExisting,
    deleteById,
    detail,
    detailError,
    loadDetail,
  };
};
